// =============================================================================
// PRODUCT ANALYTICS — the authenticated half of measurement.
//
// Implements `product-analytics/ROUND2-EVENT-DICTIONARY.md` verbatim. iOS and
// Android implement the same file. 🔴 THE STRINGS IN IT ARE THE SCHEMA: the
// dashboard groups by string, there is no join table and no alias map, so a name
// invented here silently becomes a number nobody can join. Do not rename, improve
// or extend the vocabulary in this file — change the dictionary first.
//
// 🔴 WHY THIS IS NOT IN tracking.js, THOUGH IT BORROWS ITS ONE GOOD IDEA.
//    tracking.js answers "where did this ANONYMOUS visitor come from" — it mints
//    `am_visitor_id`, sends `credentials: 'omit'` and NO Authorization header, and
//    its own header states it never touches `token`/`user`/`upcoming_event`.
//    This module answers "what did this SIGNED-IN user do" and is identified
//    entirely by the JWT. Two identity models in one file is how one of them ends
//    up bent to fit the other, and link tracking is live and earning — so it gets
//    a second, clearly-separate sender instead.
//
// 🔑 KEEPALIVE IS THE ONE THING COPIED ACROSS, AND IT IS LOAD-BEARING. This app
//    navigates with `window.location.href` constantly (the house rule after any
//    auth or destructive action), and an in-flight XHR is CANCELLED by navigation.
//    `keepalive: true` lets the request outlive the document. That is why this is
//    `fetch` and not `axios`, exactly as in tracking.js's `post`.
//
// 🔒 TOTAL, LIKE tracking.js. Never throws, never retries, never surfaces anything,
//    never blocks the UI. Every response — including a 400, a 500 or the
//    rate-limited `{success, logged: false}` — is success as far as this file is
//    concerned. There is nothing a caller can or should handle.
// =============================================================================

import { readLocal, readSession, writeSession } from './safeStorage';

const API = process.env.REACT_APP_SERVER_API;

// ── The vocabulary, as constants ─────────────────────────────────────────────
// Exported as constants rather than spelled at each call site on purpose: a typo
// in a literal does not fail, it invents a screen that quietly splits a number in
// the dashboard. Only the rows the dictionary marks as existing on WEB are here.
// ⚠️ Web has no `community` / `friends` / `messages` / `profile` / `explore` /
//    `create_event` / `itinerary` screen. Do NOT map a substitute onto those names.
export const SCREEN = Object.freeze({
    HOME: 'home',
    EVENT_DETAILS: 'event_details',
    MANAGE_SUBSCRIPTION: 'manage_subscription',
    AFFILIATE_DASHBOARD: 'affiliate_dashboard',
});

/**
 * POST /events/track, fire and forget.
 *
 * 🔴 SIGNED-OUT VISITORS EMIT NOTHING. `middleware/authorization` runs first and
 *    403s without a token, so emitting while signed out would bury the real rows
 *    in noise for no data. We return before any request is made — not a failed
 *    one, not a 403, nothing in the Network tab at all.
 *
 * ⚠️ `Bearer ` prefix is mandatory: authorization does `header.substring(7)`
 *    unconditionally, so a bare token silently loses 7 characters and 401s.
 *
 * ⚠️ `metadata` keys are LITERAL. `middleware/iosCamelCaseConversion` is shallow —
 *    it rewrites top-level keys only and passes the nested bag through by
 *    reference — so spell every metadata key in snake_case by hand and never run
 *    this object through a key-transforming serializer.
 *
 * The server stamps `created_at` from its own clock and derives `platform` from
 * the User-Agent; `platform: 'web'` is sent only as the fallback the dictionary
 * asks for. Nothing is buffered for a later flush — buffered events would all
 * collapse onto the flush time.
 */
export function trackEvent(actionType, metadata) {
    try {
        if (!API) return null; // no REACT_APP_SERVER_API baked in — never a request to "undefined/..."
        if (typeof fetch !== 'function') return null;

        const token = readLocal('token');
        if (!token) return null; // signed out — see above

        return fetch(API + '/events/track', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
            body: JSON.stringify({
                action_type: actionType,
                metadata: metadata || {},
                platform: 'web',
            }),
            keepalive: true,
        }).catch(() => null);
    } catch (_) {
        return null;
    }
}

// -----------------------------------------------------------------------------
// 🔒 THE ONE DEDUPE RULE — a single module-scope string, and it REPLACES every
//    per-screen guard. Emit only when the new screen differs from the last one
//    emitted, then assign it.
//
//    On web the thing it neutralises is `React.StrictMode` (on in src/index.js),
//    which double-invokes mount in development — and this app is almost entirely
//    CLASS components, so the natural instrumentation point is componentDidMount,
//    which StrictMode re-runs. iOS has the same problem from `.onAppear` refiring
//    on back-navigation, Android from `LaunchedEffect(Unit)` re-running after
//    navigateToSingleTop disposes a composition.
//
// 🔴 DO NOT ADD PER-COMPONENT "did I already fire" FLAGS ON TOP OF THIS. They
//    interact badly with the above and make two surfaces disagree. Consecutive
//    duplicates are suppressed; a genuine return IS a new view — home →
//    event_details → home correctly emits `home` twice, because event_details sat
//    between them.
// -----------------------------------------------------------------------------

let lastScreenEmitted = null;

/**
 * `screen_view` for one of the SCREEN constants. Deduped as described above.
 */
export function trackScreen(name) {
    try {
        if (!name || name === lastScreenEmitted) return;
        lastScreenEmitted = name;
        trackEvent('screen_view', { screen: name });
    } catch (_) { /* never let measurement break a render */ }
}

// -----------------------------------------------------------------------------
// app_open — once per browser TAB SESSION.
//
// 🔴 `cold: true` MUST MEAN THE SAME THING ON ALL THREE SURFACES OR THE COLUMN IS
//    MEANINGLESS. On iOS and Android it is a new PROCESS. The naive web analogue is
//    a page load — and that is WRONG here, because this app does full-document
//    navigations with `window.location.href` all over the place (the same reason
//    the sender needs `keepalive`). A module-scope flag dies with the document, so
//    every hard nav would mint a fresh module and fire another `app_open`: web
//    would report "page loads" into the same column iOS and Android report
//    "launches" into, and nobody reading the chart would know.
//
// 🔑 sessionStorage IS THE CLOSEST TRUE ANALOGUE TO A COLD LAUNCH. It is scoped to
//    the TAB, survives same-tab navigation and reloads, and a genuinely new tab
//    gets a fresh one. That is "the user opened the app", as nearly as a browser
//    can express it.
//
// BOTH GUARDS ARE LOAD-BEARING, AND THEY COVER DIFFERENT THINGS:
//   · the sessionStorage key — across documents, i.e. every hard navigation.
//   · the module-scope flag  — WITHIN one document, i.e. React.StrictMode's
//     double-invoke in development. Deleting either one reintroduces a duplicate.
//
// ⚠️ Known and accepted: Chrome COPIES sessionStorage into a duplicated tab and on
//    session restore, so those do not emit. That is the right answer anyway —
//    neither is the user opening the app.
//
// 🔒 The key is `am_`-prefixed to match the house convention and does not collide
//    with the four tracking.js owns (`am_visitor_id`, `am_last_source`,
//    `am_click_id`, `am_visit_sent`). This module owns this one.
// -----------------------------------------------------------------------------

const KEY_APP_OPEN_SENT = 'am_app_open_sent';

let appOpenSent = false;

export function trackAppOpen() {
    try {
        if (appOpenSent) return;                             // this document (StrictMode)
        if (readSession(KEY_APP_OPEN_SENT) === '1') {        // an earlier document in this tab
            appOpenSent = true;
            return;
        }

        // 🔴 WRITE THE GUARD FIRST, AND EMIT ONLY IF IT STUCK. When storage is
        //    unavailable — Safari private browsing, "block all cookies", where the
        //    ACCESS ITSELF throws — we cannot dedupe, and the failure mode of
        //    emitting anyway is exactly the bug this block exists to prevent: one
        //    `app_open` per page load, silently inflating the only launch number we
        //    have. Skipping is the safe direction; an under-count is visible as a
        //    gap, an over-count looks like growth.
        //
        //    In practice this is unreachable: a store that throws on sessionStorage
        //    throws on localStorage too, so there is no JWT and trackEvent would
        //    already have returned. It is written this way so that stays true if
        //    either half ever changes. readSession/writeSession never throw.
        appOpenSent = true;
        if (!writeSession(KEY_APP_OPEN_SENT, '1')) return;

        trackEvent('app_open', { cold: true });
    } catch (_) { /* never let measurement break startup */ }
}
